import React, { useState } from 'react'
import { Input, Card, Button, Collapse, Space } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import { FormField, ArrayItemCard, RemoveButton } from './shared'
import { useFieldPath } from './TabContext'
import { get } from 'lodash'

const { TextArea } = Input
const { Panel } = Collapse

export const FaqSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<any>()
  const [newTabKey, setNewTabKey] = useState('')
  const [newTabLabel, setNewTabLabel] = useState('')

  const titlePath = useFieldPath('sections.faq.title')
  const faqTabsPath = useFieldPath('sections.faq.faqTabs')
  const htmlPath = useFieldPath('sections.faq.html')

  const faqTabs = get(values, faqTabsPath, {})
  const tabKeys = Object.keys(faqTabs)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldValue(e.target.name, e.target.value)
  }

  const addTab = () => {
    if (newTabKey && newTabLabel && !faqTabs[newTabKey]) {
      setFieldValue(`${faqTabsPath}.${newTabKey}`, {
        label: newTabLabel,
        items: [],
      })
      setNewTabKey('')
      setNewTabLabel('')
    }
  }

  const removeTab = (tabKey: string) => {
    const updatedTabs = { ...faqTabs }
    delete updatedTabs[tabKey]
    setFieldValue(faqTabsPath, updatedTabs)
  }

  const addItem = (tabKey: string) => {
    const currentItems = faqTabs[tabKey]?.items || []
    setFieldValue(`${faqTabsPath}.${tabKey}.items`, [
      ...currentItems,
      { id: `faq-${Date.now()}`, question: '', answer: '', answerHtml: '' },
    ])
  }

  const removeItem = (tabKey: string, itemIndex: number) => {
    const currentItems = faqTabs[tabKey]?.items || []
    const updatedItems = currentItems.filter((_, i) => i !== itemIndex)
    setFieldValue(`${faqTabsPath}.${tabKey}.items`, updatedItems)
  }

  return (
    <Card>
      <FormField>
        <label>Título da Seção FAQ</label>
        <Input
          name={titlePath}
          value={get(values, titlePath, '')}
          onChange={handleChange}
          placeholder="Ex: Dúvidas frequentes"
        />
      </FormField>

      <FormField>
        <label>Texto inferior (HTML opcional)</label>
        <TextArea
          name={htmlPath}
          value={get(values, htmlPath, '')}
          onChange={handleChange}
          rows={5}
          placeholder="Cole aqui o HTML que será exibido no bloco 'Ainda com dúvidas?'"
        />
      </FormField>

      <h4>Abas do FAQ</h4>
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input
            placeholder="Chave da aba (ex: certificado)"
            value={newTabKey}
            onChange={(e) => setNewTabKey(e.target.value)}
          />
          <Input
            placeholder="Label da aba (ex: Certificado)"
            value={newTabLabel}
            onChange={(e) => setNewTabLabel(e.target.value)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={addTab}
            disabled={!newTabKey || !newTabLabel}
          >
            Adicionar Aba
          </Button>
        </div>
      </Space>

      {tabKeys.length === 0 ? (
        <p style={{ color: '#999' }}>Nenhuma aba criada. Adicione uma aba acima.</p>
      ) : (
        <Collapse accordion>
          {tabKeys.map((tabKey) => (
            <Panel
              header={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>
                    {faqTabs[tabKey]?.label || tabKey} ({faqTabs[tabKey]?.items?.length || 0} itens)
                  </span>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation()
                      removeTab(tabKey)
                    }}
                  >
                    Remover Aba
                  </Button>
                </div>
              }
              key={tabKey}
            >
              <FormField>
                <label>Label da Aba</label>
                <Input
                  value={faqTabs[tabKey]?.label}
                  onChange={(e) => setFieldValue(`${faqTabsPath}.${tabKey}.label`, e.target.value)}
                  placeholder="Label que aparece na aba"
                />
              </FormField>

              <h5>Itens do FAQ ({tabKey})</h5>
              {faqTabs[tabKey]?.items?.map((item, itemIndex) => (
                <ArrayItemCard key={itemIndex}>
                  <RemoveButton>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeItem(tabKey, itemIndex)}
                    />
                  </RemoveButton>

                  <FormField>
                    <label>ID do Item</label>
                    <Input
                      value={item.id}
                      onChange={(e) =>
                        setFieldValue(
                          `${faqTabsPath}.${tabKey}.items.${itemIndex}.id`,
                          e.target.value
                        )
                      }
                      placeholder="ID único (ex: cert-1)"
                    />
                  </FormField>

                  <FormField>
                    <label>Pergunta</label>
                    <Input
                      value={item.question}
                      onChange={(e) =>
                        setFieldValue(
                          `${faqTabsPath}.${tabKey}.items.${itemIndex}.question`,
                          e.target.value
                        )
                      }
                      placeholder="Ex: O certificado é válido?"
                    />
                  </FormField>

                  <FormField>
                    <label>Resposta</label>
                    <TextArea
                      value={item.answer}
                      onChange={(e) =>
                        setFieldValue(
                          `${faqTabsPath}.${tabKey}.items.${itemIndex}.answer`,
                          e.target.value
                        )
                      }
                      rows={3}
                      placeholder="Resposta da pergunta"
                    />
                  </FormField>
                  <FormField>
                    <label>Resposta (HTML opcional)</label>
                    <TextArea
                      value={item.answerHtml}
                      onChange={(e) =>
                        setFieldValue(
                          `${faqTabsPath}.${tabKey}.items.${itemIndex}.answerHtml`,
                          e.target.value
                        )
                      }
                      rows={4}
                      placeholder="Cole HTML aqui. Se preencher, substitui a 'Resposta' acima."
                    />
                  </FormField>
                </ArrayItemCard>
              ))}

              <Button type="dashed" onClick={() => addItem(tabKey)} block icon={<PlusOutlined />}>
                Adicionar Item
              </Button>
            </Panel>
          ))}
        </Collapse>
      )}
    </Card>
  )
}
